import { amplitudeLib } from 'freedom-shared';

export type NomadLabelSource = 'direct_flights';

export type MilesCardBlockState = 'add' | 'edit' | 'list' | 'multi';

export type MilesCardAddSource = 'block' | 'card_list' | 'multi_list' | 'form';

export type MilesCardSaveMode = 'add' | 'edit';

export type MilesCardSelectFlow = 'single' | 'multi';

interface MilesCardBlockShowParams {
  offerId: string;
  passengerCount: number;
  programsCount: number;
  hasNomadProgram: boolean;
  hasSavedCards: boolean;
}

interface MilesCardBlockClickParams {
  offerId: string;
  state: MilesCardBlockState;
}

interface MilesCardAddClickParams {
  offerId: string;
  source: MilesCardAddSource;
}

/**
 * Card numbers in the payloads below must already be masked with
 * `maskMilesCardNumber` (features/miles-cards) — only the last 4 characters
 * may reach Amplitude, never the raw value.
 */
interface MilesCardSavedParams {
  offerId: string;
  mode: MilesCardSaveMode;
  programTitle: string;
  maskedCardNumber: string;
}

interface MilesCardDeletedParams {
  offerId: string;
  programTitle: string;
  maskedCardNumber: string;
}

interface MilesCardSelectedParams {
  offerId: string;
  flow: MilesCardSelectFlow;
  selectedCount: number;
  maskedCardNumbers: string[];
}

export const nomadLabelClick = (source: NomadLabelSource) =>
  amplitudeLib.logEvent('avfree_nomad_label_click', { source });

// The booking page re-renders the block on passenger edits and sheet
// toggles; the impression must count once per offer, not per render.
const blockShowSentOfferIds = new Set<string>();

export const milesCardBlockShow = (params: MilesCardBlockShowParams) => {
  if (blockShowSentOfferIds.has(params.offerId)) {
    return;
  }
  blockShowSentOfferIds.add(params.offerId);

  amplitudeLib.logEvent('avfree_miles_card_block_show', {
    offer_id: params.offerId,
    passenger_count: params.passengerCount,
    programs_count: params.programsCount,
    has_nomad_program: params.hasNomadProgram,
    has_saved_cards: params.hasSavedCards,
  });
};

export const milesCardBlockClick = (params: MilesCardBlockClickParams) =>
  amplitudeLib.logEvent('avfree_miles_card_block_click', {
    offer_id: params.offerId,
    state: params.state,
  });

export const milesCardAddClick = (params: MilesCardAddClickParams) =>
  amplitudeLib.logEvent('avfree_miles_card_add_click', {
    offer_id: params.offerId,
    source: params.source,
  });

export const milesCardSaved = (params: MilesCardSavedParams) =>
  amplitudeLib.logEvent('avfree_miles_card_saved', {
    offer_id: params.offerId,
    mode: params.mode,
    program_title: params.programTitle,
    card_number: params.maskedCardNumber,
  });

export const milesCardDeleted = (params: MilesCardDeletedParams) =>
  amplitudeLib.logEvent('avfree_miles_card_deleted', {
    offer_id: params.offerId,
    program_title: params.programTitle,
    card_number: params.maskedCardNumber,
  });

export const milesCardSelected = (params: MilesCardSelectedParams) =>
  amplitudeLib.logEvent('avfree_miles_card_selected', {
    offer_id: params.offerId,
    flow: params.flow,
    selected_count: params.selectedCount,
    card_numbers: params.maskedCardNumbers,
  });
